import { IUndoRedoService } from "../../../../platform/undo/common/undo.js";
import { IKeyboardService } from "../../../../platform/keyboard/common/keyboardService.js";
import { IContextService } from "../../../../platform/context/common/contextService.js";
import { IKeybindingService } from "../../../../platform/keybinding/common/keybindingService.js";
import { createDecorator, registerSingleton } from "../../../../platform/instantiation/common/registry.js";
import { SyncDescriptor } from "../../../../platform/instantiation/common/descriptors.js";
import { Disposable, toDisposable } from "../../../../platform/../base/common/lifecycle.js";
import { IIdentitiyService } from "../../../../platform/auth/common/auth.js";
import { IFileService } from "../../../../platform/files/common/files.js";
import { IInstantiationService } from "../../../../platform/instantiation/common/instantiation.js";
import { IAIService } from "../../../../platform/ai/common/ai.js";
import { ISecretStorageService } from "../../../../platform/secrets/common/secrets.js";
import { IWebGLService } from "../../../../platform/webgl/common/webgl.js";
import { IProjectService } from "../../project/common/project.js";
import { ILogService } from "../../../../platform/log/common/log.js";
import { IUserConfiguration } from "../../user-configuration/common/userConfiguration.js";
import { BUILD_HASH, BUILD_TIME, BUILD_ENVIRONMENT, isNeedleEditorHTMLElement, BUILD_SEMVER } from "../../../../base/common/needle.js";
import { IAnalyticsService } from "../../../../platform/analytics/common/analytics.js";
import { IFileDialogService } from "../../../../platform/dialogs/common/dialogs.js";
import { CommandsRegistry, ICommandService } from "../../../../platform/commands/common/commands.js";
import { MenuId, MenuRegistry } from "../../../../platform/actions/common/actions.js";
import { MenubarControl } from "../../../services/titlebar/menubarControl.js";
import { localize } from "../../../../base/common/nls.js";
import { IHierarchyService } from "../../../../platform/hierarchy/common/hierarchy.js";
import { IPropertyService } from "../../../../platform/properties/common/properties.js";
import { Emitter, Event } from "../../../../base/common/events.js";
import { ContextKeyExpr } from "../../../../platform/contextkey/common/contextkey.js";
import { isLocalhostOrIpAddress } from "../../../../base/browser/url.js";
import { IProductService } from "../../../../platform/product/common/productService.js";
import { IQuickInputService } from "../../../../platform/quickinput/common/quickInput.js";


// #region Register Menu Items
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    title: "Create",
    group: "1_create",
    order: 0,
    submenu: MenuId.ExplorerContextCreateSubmenu,
    icon: {
        id: "file"
    }
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    title: "Copy",
    group: "2_copy",
    order: 2,
    submenu: MenuId.ExplorerContextCopySubmenu,
    icon: {
        id: "copy"
    }
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    title: "Export",
    group: "3_export",
    order: 3,
    submenu: MenuId.ExplorerContextExportSubmenu,
    icon: {
        id: "export"
    }
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    title: "Experimental",
    group: "4_experimental",
    order: 4,
    submenu: MenuId.ExplorerContextExperimentalSubmenu,
    icon: {
        id: "experimental"
    }
});


// #region Application
export interface IApplication {

    readonly onDidChange: Event<{ active?: boolean }>;

    get isActive(): boolean;

    readonly mainInstantiationService: IInstantiationService;

    readonly productService: IProductService;
    readonly undo: IUndoRedoService;
    readonly logger: ILogService;
    readonly keyboard: IKeyboardService;
    readonly fileservice: IFileService;
    readonly commands: ICommandService;
    readonly context: IContextService;
    readonly keybindings: IKeybindingService;
    readonly identitiy: IIdentitiyService | null;
    readonly secretStorage: ISecretStorageService;
    readonly ai: IAIService;
    readonly webgl: IWebGLService | null;
    readonly userConfiguration: IUserConfiguration;
    readonly project: IProjectService | null;
    readonly fileDialogService: IFileDialogService;
    readonly hierarchyService: IHierarchyService | null;
    readonly propertyService: IPropertyService | null;
    readonly menubarControl: MenubarControl;
}

interface ApplicationChangedEvent {
    /** true when active state changed */
    active?: boolean;
}

export class Application extends Disposable implements IApplication {

    private readonly _onDidChange = this._register(new Emitter<ApplicationChangedEvent>());
    readonly onDidChange = this._onDidChange.event;

    private _isActive: boolean = true;
    get isActive(): boolean {
        return this._isActive;
    }
    set isActive(value: boolean) {
        if (this._isActive !== value) {
            this._isActive = value;
            // Active indicates that 'active' state has changed
            this._onDidChange.fire({ active: true });
        }
    }

    constructor(
        active: boolean = true,
        @IInstantiationService readonly mainInstantiationService: IInstantiationService,
        @IProductService readonly productService: IProductService,
        @IUndoRedoService readonly undo: IUndoRedoService,
        @ILogService readonly logger: ILogService,
        @IKeyboardService readonly keyboard: IKeyboardService,
        @IFileService readonly fileservice: IFileService,
        @ICommandService readonly commands: ICommandService,
        @IContextService readonly context: IContextService,
        @IKeybindingService readonly keybindings: IKeybindingService,
        @IIdentitiyService readonly identitiy: IIdentitiyService | null,
        @ISecretStorageService readonly secretStorage: ISecretStorageService,
        @IAIService readonly ai: IAIService,
        @IWebGLService readonly webgl: IWebGLService | null,
        @IProjectService readonly project: IProjectService,
        @IUserConfiguration readonly userConfiguration: IUserConfiguration,
        @IAnalyticsService readonly analytics: IAnalyticsService | null,
        @IFileDialogService readonly fileDialogService: IFileDialogService,
        @IHierarchyService readonly hierarchyService: IHierarchyService | null,
        @IPropertyService readonly propertyService: IPropertyService | null,
    ) {
        super();
        this._isActive = active;

        this.registerApplicationCommands();
        this.registerEventListeners();
        this.logger.debug("Application initialized.");
        this.menubarControl = this.mainInstantiationService.createInstance(new SyncDescriptor(MenubarControl));

        // Detection for chrome when the editor becomes active by selecting a node
        // Only checking for nodes added is also triggered by e.g. the webxr extension
        if (this.hierarchyService) {
            Event.once(this.hierarchyService.onNodeAdded)(_evt => {
                const off = this.hierarchyService?.onSelectedNodesDidChange(evt => {
                    if (evt.selection.length > 0) {
                        this.isActive = true;

                        off?.dispose();
                        this.analytics?.logEvent("pageview", { reason: "node_selected", build: BUILD_ENVIRONMENT, hash: BUILD_HASH, time: BUILD_TIME, version: BUILD_SEMVER });
                        if (isLocalhostOrIpAddress(window.location.href)) {
                            this.analytics?.logEvent("local-dev", { url: window.location.href, build: BUILD_ENVIRONMENT, hash: BUILD_HASH, time: BUILD_TIME, version: BUILD_SEMVER });
                        }
                    }
                });
            });
        }

        this.mainInstantiationService.invokeFunction(accessor => {

            const quickinput = accessor.getIfExists(IQuickInputService);
            if (quickinput) {
                this.keybindings.registerKeybinding({
                    id: "keybinding.quickOpen",
                    key: ["Ctrl+P", "Ctrl+E"],
                    commandId: "quickinput.show",
                    preventDefault: true,
                });
                this.keybindings.registerKeybinding({
                    id: "keybinding.quickOpen-command",
                    key: "Ctrl+Shift+P",
                    commandId: "quickinput.show",
                    preventDefault: true,
                    args: { prefix: '>' }
                });
            }

        })
    }

    readonly menubarControl: MenubarControl;

    private registerApplicationCommands(): void {
        // Register undo command
        this._register(CommandsRegistry.registerCommand({
            id: 'edit.undo',
            label: 'Undo',
            canExecute: () => ({
                canExecute: this.undo.canUndo
            }),
            execute: () => this.undo.undo()
        }));
        this.keybindings.registerKeybinding({
            id: "keybinding.undo",
            key: "Ctrl+Z",
            commandId: "edit.undo",
        });
        // Register redo command
        this._register(CommandsRegistry.registerCommand({
            id: 'edit.redo',
            label: 'Redo',
            canExecute: () => ({
                canExecute: this.undo.canRedo
            }),
            execute: () => this.undo.redo()
        }));
        this.keybindings.registerKeybinding({
            id: "keybinding.redo",
            key: "Ctrl+Y",
            commandId: "edit.redo",
        });
        this.keybindings.registerKeybinding({
            id: "keybinding.redo-alt",
            key: "Ctrl+Shift+Z",
            commandId: "edit.redo",
        });

        // Undo redo menu items
        MenuRegistry.appendMenuItem(MenuId.MenubarEditMenu, {
            command: {
                id: "edit.undo",
                title: 'Undo',
                icon: { id: "undo" },
                precondition: ContextKeyExpr.has("undoRedo.hasUndo"),
            },
            title: {
                value: 'Undo',
                original: 'Undo',
                mnemonicTitle: localize({ key: 'mUndo', comment: ['&& denotes a mnemonic'] }, "&&Undo"),
            },
            order: 0,
            group: 'undo'
        });
        MenuRegistry.appendMenuItem(MenuId.MenubarEditMenu, {
            command: {
                id: "edit.redo",
                title: 'Redo',
                icon: { id: "redo" },
                precondition: ContextKeyExpr.has("undoRedo.hasRedo"),
            },
            title: {
                value: 'Redo',
                original: 'Redo',
                mnemonicTitle: localize({ key: 'mRedo', comment: ['&& denotes a mnemonic'] }, "&&Redo"),
            },
            order: 1,
            group: 'undo'
        });


        this._register(CommandsRegistry.registerCommand({
            id: 'ui.toggleLayout',
            label: 'Toggle UI Layout',
            execute: () => {
                console.log("Toggling UI Layout", this.userConfiguration);
                switch (this.userConfiguration.uiLayout) {
                    case "minimal":
                        this.userConfiguration.uiLayout = "default";
                        break;
                    case "default":
                    default:
                        this.userConfiguration.uiLayout = "minimal";
                        break;
                }
            }
        }));
        this.keybindings.registerKeybinding({
            id: "keybinding.toggleUI",
            key: "Ctrl+U",
            commandId: "ui.toggleLayout",
        });
    }


    private registerEventListeners(): void {

        // Global input event listener to prevent unwanted interactions outside of the editor UI
        const inputEventTypes = [
            'keydown',
            'keyup',
            'keypress',
            'mousedown',
            'mouseup',
            'click',
            'dblclick',
            // 'pointerdown', // TODO: we need to forward these elements to our own event system to make fly controls work
            // 'pointermove',
            // 'pointerup',
            'wheel',
            'touchstart',
            'touchend',
            'touchmove',
        ];

        for (const eventType of inputEventTypes) {
            window.addEventListener(eventType, this.onInputEvent, {
                capture: true,
                passive: false
            });
            this._register(toDisposable(() => {
                window.removeEventListener(eventType, this.onInputEvent);
            }));
        }
    }


    private onInputEvent = (event: InputEvent | KeyboardEvent) => {
        // Prevent input events when editor camera is active and the event target is outside of the needle editor UI
        // This is a workaround to prevent unwanted interactions with other UI elements when using the editor camera
        const editorCameraIsActive = this.webgl?.getAllEngines().some(e => e.editorCamera);
        if (editorCameraIsActive) {

            if (event.type === "keydown") {
                const keyboardEvent = event as KeyboardEvent;
                // Allow modifier keys to propagate
                if (keyboardEvent.key === "Shift" || keyboardEvent.key === "Control" || keyboardEvent.key === "Alt" || keyboardEvent.key === "Meta") {
                    this.logger.debug(`Allowed modifier key event '${event.type}'`);
                    return true;
                }
                // Allow F1-F12 keys to propagate
                if (keyboardEvent.key.startsWith("F") && !isNaN(parseInt(keyboardEvent.key.substring(1)))) {
                    this.logger.debug(`Allowed function key event '${event.type}'`);
                    return true;
                }
                if (this.keyboard.isKeyPressed("Control") || this.keyboard.isKeyPressed("Meta")) {
                    // Allow key combinations with Control or Meta (Cmd) to propagate
                    this.logger.debug(`Allowed key combination event '${event.type}' with Control/Meta`);
                    return true;
                }
            }

            if (!isNeedleEditorHTMLElement(event.target)) {
                // is up event
                if (event.type === 'keyup' || event.type === 'pointerup' || event.type === 'mouseup' || event.type === 'touchend') {
                    // Let up events pass through to avoid stuck keys/buttons
                    this.logger.trace(`Allowed input event '${event.type}' to avoid stuck keys/buttons.`);
                    return true;
                }

                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                this.logger.trace(`Blocked input event '${event.type}' outside of needle editor UI.`, event.target);
                return false;
            }
            else {
                // Wheel events ON the needle editor UI
                const isWheelEvent = event.type === 'wheel';
                if (isWheelEvent) {
                    event.stopPropagation();
                    this.logger.trace(`Allowed wheel event '${event.type}' on needle editor UI.`);
                    return true;
                }
            }
        }
    }


}

export const IApplication = createDecorator<IApplication>('application');

registerSingleton(IApplication, new SyncDescriptor(Application));

